import React, { useContext, useEffect, useMemo, useState } from 'react';
import { StarberryComponentContext } from '@starberry/components';
import { Stack } from '@chakra-ui/react';
import jwt_decode from 'jwt-decode';
import { isAfter } from 'date-fns';

import { useAuthState } from 'core/services/store/auth/hooks';
import { useProfileState } from 'core/services/store/profile/hooks';
import { getToken } from 'core/services/store/utils';
import { updateToken } from 'core/services/store/api';

import PageHero from './PageHero';
import { Routes } from '../../routes';
import { loginRedirect } from 'core/services/store/auth/services';

const Dashboard = ({ children, ...props }) => {
  const { services: authServices, state } = useAuthState();
  const { services: profileServices } = useProfileState();
  const [loading, setLoading] = useState(true);
  const context = useContext(StarberryComponentContext);

  const isAuthenticated = useMemo(() => authServices.isAuthenticated(), []);
  useEffect(() => {
    if (window.location.search.indexOf('id_token') !== -1) {
      const provider = window.location.search.indexOf('google') !== -1 ? 'google' : 'facebook'; // temporary solution
      authServices.providerAuthLogin(provider, window.location.search).then(() => {
        if (Routes[context?.constants?.indexPage]) {
          window.location.search = '';
          window.location.href = Routes[context?.constants?.indexPage];
          return;
        }
        setLoading(false);
      });
      return;
    }

    loginRedirect(authServices, state);

    if (isAuthenticated) {
      const token = getToken();
      updateToken(token);

      profileServices.getUserProfile();

      const decoded = jwt_decode(token);
      if (isAfter(new Date(), new Date(decoded.exp * 1000))) {
        window.location.href = Routes.LoginPage;
        return;
      }
    } else {
      window.location.href = `${Routes.LoginPage}?r=${window.location.href.replace(
        window.location.origin,
        ''
      )}`;
      return;
    }

    if (window.location.search.indexOf('?r=') !== -1) {
      window.location.href = window.location.search.split('=')[1];
      return;
    }

    if (
      window.location.pathname === '/' &&
      Routes[context?.constants?.indexPage] &&
      Routes[context?.constants?.indexPage] !== '/'
    ) {
      window.location.href = Routes[context?.constants?.indexPage];
      return;
    }

    setLoading(false);
  }, [isAuthenticated]);

  if (loading) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0} flex={1} {...props}>
      {isAuthenticated && children}
    </Stack>
  );
};

const DashboardModule = {
  component: Dashboard,
  content: null,
  title: 'My Account',
  props: {
    minHeight: ['calc(100vh - 65px)', null, '100vh'],
  },
  children: ({ companyLogo, sideNav, content = [], placement = 'left', title, titleProps }, context) => {
    const order = [
      { $: 'DesktopSideMenu', companyLogo, sideNav },
      {
        $: 'Stack',
        props: {
          flex: 1,
          spacing: [5, null, 12, null, 14],
          mt: 0,
          direction: 'column',
        },
        children: [
          {
            $: 'AccountHeader',
            placement,
            title,
            companyLogo,
            sideNav,
          },
          {
            $: 'Stack',
            props: {
              flex: 1,
              direction: 'column',
              spacing: [5, null, 12, null, 14],
            },
            children: [
              title && {
                $: 'ContentSection',
                props: {
                  px: context.theme.space[12],
                  spacing: 0,
                },
                children: [
                  { $: PageHero, primary: [{ $: 'H3', props: { as: 'h1', ...titleProps }, children: title }] },
                ],
              },
              ...content,
            ],
          },
          {
            $: 'Footer',
          },
          {
            $: 'MobileBottomNav',
            sideNav,
          },
        ],
      },
    ];
    return placement === 'left' ? order : order.reverse();
  },
};

export default DashboardModule;
